import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';

import FWorkbookImport from '../core/FWorkbookImport';

import {
  uploadBieHistoricalWorkbook,
  resolveValidateBieHistoricalWorkbook,
} from './biehistoricalworkbook.apis';

import {
  BieHistoricalWorkbookUploadResult,
  BieHistoricalWorkbookValidateResult,
} from './biehistoricalworkbook.models';

import {
  downloadHistoricalTemplate,
} from './biehistoricalworkbook.client';

type Props = {
  planningcycleid: number;
  historicaldatasetid?: number | null;
  onCompleted?: (result: any) => void;
  showNotification: any;
};

type State = {
  latestUpload: BieHistoricalWorkbookUploadResult | null;
  downloading: boolean;
  resolving: boolean;
  resolveError: string;
  validationResult: BieHistoricalWorkbookValidateResult | null;
};

class HistoricalWorkbookPanel extends React.PureComponent<Props, State> {
  state: State = {
    latestUpload: null,
    downloading: false,
    resolving: false,
    resolveError: '',
    validationResult: null,
  };

  private handleDownloadTemplate = async (): Promise<void> => {
    try {
      this.setState({ downloading: true });

      await downloadHistoricalTemplate(
        this.props.planningcycleid,
        this.props.historicaldatasetid ?? null,
      );
    } catch (error) {
      this.props.showNotification(
        error instanceof Error
          ? error.message
          : 'Template download failed.',
        'error',
      );
    } finally {
      this.setState({ downloading: false });
    }
  };

  private upload = async (file: File) => {
    const result = await uploadBieHistoricalWorkbook({
      file,
      planningcycleid: this.props.planningcycleid,
      historicaldatasetid: this.props.historicaldatasetid ?? null,
    });

    this.setState({
      latestUpload: result,
      validationResult: null,
      resolveError: '',
    });

    return result;
  };

  private resolveValidate = async () => {
    const upload = this.state.latestUpload;

    const historicaldatasetid =
      upload?.historicaldatasetid ??
      this.props.historicaldatasetid ??
      null;

    this.setState({
      resolving: true,
      resolveError: '',
    });

    try {
      const result = await resolveValidateBieHistoricalWorkbook({
        planningcycleid: this.props.planningcycleid,
        historicaldatasetid,
        checksum: upload?.checksum ?? null,
      });

      this.setState({
        resolving: false,
        validationResult: result,
      });

      this.props.onCompleted?.(result);
    } catch (error: any) {
      this.setState({
        resolving: false,
        resolveError:
          error?.message ||
          'Unable to resolve and validate the workbook.',
      });
    }
  };

  render() {
    const {
      latestUpload,
      resolving,
      resolveError,
      validationResult,
    } = this.state;

    return (
      <Box>
        <FWorkbookImport
          title="Historical Data Workbook"
          description="Use the controlled template to import Planning Cycle historical facts."
          onDownloadTemplate={this.handleDownloadTemplate}
          onUpload={this.upload}
        />

        {(latestUpload || this.props.historicaldatasetid) && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={
                resolving
                  ? <CircularProgress size={18} />
                  : <AutorenewOutlinedIcon />
              }
              disabled={resolving}
              onClick={() => void this.resolveValidate()}
            >
              Resolve & Validate
            </Button>
          </Box>
        )}

        {!!resolveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {resolveError}
          </Alert>
        )}

        {!!validationResult && (
          <Alert
            severity={
              validationResult.usable
                ? validationResult.warningRows > 0
                  ? 'warning'
                  : 'success'
                : 'error'
            }
            sx={{ mt: 2 }}
          >
            {validationResult.usable
              ? `${validationResult.committedRows} historical fact(s) are now usable.`
              : `${validationResult.blockingIssues} blocking issue(s) remain.`}
          </Alert>
        )}
      </Box>
    );
  }
}

export default HistoricalWorkbookPanel;
